import {useStore} from 'vuex';
import {computed} from 'vue';
import {default as dayjs} from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {pluralize} from '@/utils/text';
import {useCompany} from '@/composables/useCompany.ts';
import type {SubscriptionPlan} from '@/types/subscription';
import {CENTS_TO_DOLLARS} from '@/constants/money.ts';

dayjs.extend(isSameOrBefore);

export interface planSupportedFeatureCountsInterface {
    listings: number;
    locations: number;
    carSharingConnections: number;
    smartCarConnections: number;
    orders: number;
    seats: number;
}

export function useSubscription() {
    const store = useStore();
    const {company, companyCounts} = useCompany();

    const enforceSubscription = computed(() => store.getters['subscriptions/enforceSubscription']);
    const customersLoading = computed(() => store.getters['renters/rentersLoading']);

    const hasActiveSubscription = computed(() => store.getters['subscriptions/hasActiveSubscription']);
    const subscriptionPlans = computed<SubscriptionPlan[]>(() => store.getters['subscriptions/plans']);
    const subscriptionPlan = computed(() => store.getters['subscriptions/subscriptionPlan']);
    const subscription = computed(() => store.getters['subscriptions/subscription']);
    const user = computed(() => store.getters['auth/user']);
    const stripeSubscriptionObject = computed(() => store.getters['stripe/stripeSubscriptionObject']);
    const subscriptionsLoading = computed(() => store.getters['subscriptions/loading']);
    const subscriptionEndDate = computed(() => store.getters['subscriptions/subscriptionEndDate']);
    const hasPastDueSubscription = computed(() => store.getters['subscriptions/hasPastDueSubscription']);
    const hasPausedSubscription = computed(() => store.getters['subscriptions/hasPausedSubscription']);

    const stripeTrialDate = computed(() => subscription.value?.trial_ends_at);
    const planFeatureCountsFromStore = computed(() => store.getters['subscriptions/planFeatureCounts'] || {});
    const planFeatureFlagsFromStore = computed(() => store.getters['subscriptions/planFeatureFlags'] || {});

    const resolveFeatureCount = (feature: keyof planSupportedFeatureCountsInterface): number => {
        const plan = subscriptionPlan.value;
        const featureMap = planFeatureCountsFromStore.value?.[feature] || {};
        const value = plan ? featureMap?.[plan] : undefined;

        return typeof value === 'number' ? value : -1;
    };

    const subscriptionStatus = computed(() => {
        const stripeStatus = subscription.value?.stripe_status;

        if (stripeStatus) return stripeStatus;

        if (user.value?.on_trial) return 'trialing';

        return 'inactive';
    });

    const trialEndDateRaw = computed(() => {
        return company.value?.trial_ends_at ?? stripeTrialDate.value;
    });

    const cancelSubscription = async () => {
        return store.dispatch('subscriptions/cancelSubscription');
    }

    const cancelSubscriptionStripePortal = async () => {
        return store.dispatch('subscriptions/cancelSubscriptionStripePortalUrl');
    }

    const endTrial = async () => {
        return store.dispatch('subscriptions/endTrial');
    }

    const switchPlan = async (plan: string) => {
        return store.dispatch('subscriptions/switchPlan', plan);
    }

    const resumeSubscription = async () => {
        return store.dispatch('subscriptions/resumeSubscription');
    }

    const addSubscriptionAddon = async (payload: { id: string, count: number }) => {
        return store.dispatch('subscriptions/addAddon', payload);
    }
    const removeSubscriptionAddon = async (addon: string | { id: string, count?: number }) => {
        return store.dispatch('subscriptions/removeAddon', addon);
    }

    const trialEndsMessage = computed(() => {
        const rawDate = trialEndDateRaw.value;

        // Check if rawDate is valid
        if (!rawDate || !dayjs(rawDate).isValid() || subscriptionStatus.value === 'active') {
            return 'Trial has ended';
        }

        const trialDate = dayjs(rawDate);
        const trialDateFmt = trialDate.format('MMMM D, YYYY');

        // Determine if the trial has already ended
        const status = trialDate.isSameOrBefore(dayjs()) ? 'ended' : 'ends';

        return `Trial ${status} ${trialDateFmt}`;
    });

    const stripeEndDate = computed(() => {
        const ends_at = subscription.value?.ends_at;
        if (!ends_at) return;
        return dayjs(ends_at).format('MMMM D, YYYY');
    });

    const inGracePeriod = computed(() => {
        const endDate = subscription.value?.ends_at;
        const status = subscriptionStatus.value;

        if (!endDate) return false;

        return !(status === 'canceled' && dayjs(endDate).diff(dayjs()) <= 0);
    });


    const inTrial = computed((): boolean => {
        const trialDate = user?.value?.on_trial;

        if (trialDate) return true;

        if (subscriptionStatus.value === 'trialing') return true;
        if (subscriptionStatus.value === 'active') return false;

        if (stripeTrialDate.value) {
            return dayjs(stripeTrialDate.value).diff(dayjs()) > 0;
        }

        return false;
    });

    const isTrialing = computed((): boolean => {
        return subscriptionStatus.value === 'trialing';
    })

    const trialEndDate = computed(() => {

        if (!trialEndDateRaw.value) {
            return 'N/A';
        }

        return dayjs(trialEndDateRaw.value).format('MMMM D, YYYY');
    });


    const currentStripeSubscriptionObjectRenewalDate = computed(() => {
        const date = stripeSubscriptionObject.value?.current_period_end;
        const status = stripeSubscriptionObject.value?.status;

        if (!date) return null;
        if (status === 'canceled') return null;

        return dayjs(date * 1000).format('MMMM D, YYYY');
    });

    const currentStripeSubscriptionObjectPlanAmount = computed(() => {
        const amount = stripeSubscriptionObject.value?.plan?.amount;

        if (!amount) return '$0';

        if (amount % 100 === 0) {
            return `$${(amount * CENTS_TO_DOLLARS).toFixed(0)}`;
        }

        return `$${(amount * CENTS_TO_DOLLARS).toFixed(2)}`;
    });

    const currentStripeSubscriptionObjectPlanInterval = computed(() => {
        return stripeSubscriptionObject.value?.plan?.interval ?? 'month';
    });

    const includedInSubscription = (topic: string): boolean => {
        if (!enforceSubscription.value || inTrial.value) return true;
        if (!hasActiveSubscription.value) return false;

        // Safely get the subscription plan in the lowercase
        const plan = subscriptionPlan.value?.toLowerCase() || '';

        // Ensure plan exists in planIncludes
        const planFeatures = planFeatureFlagsFromStore.value?.[plan] ?? null;
        if (!planFeatures) return false;

        // Check if the topic is included in the plan
        return planFeatures[topic] ?? false;
    };


    const planIncludesApi = computed(() => {
        if (!enforceSubscription.value) return true;

        if (inTrial.value && !hasActiveSubscription.value) {
            return true;
        }

        if (!hasActiveSubscription.value) {
            return false;
        }

        return planFeatureFlagsFromStore.value?.[subscriptionPlan.value]?.api ?? false;
    })

    const planIncludesESign = computed(() => {
        if (!enforceSubscription.value) return true;

        if (inTrial.value && !hasActiveSubscription.value) {
            return true;
        }

        if (!hasActiveSubscription.value) {
            return false;
        }

        return planFeatureFlagsFromStore.value?.[subscriptionPlan.value]?.esign ?? false;
    });

    const trialDaysLeft = computed((): number => {

        if (!trialEndDateRaw.value) {
            return 0;
        }

        const daysLeft = dayjs(trialEndDateRaw.value).diff(dayjs(), 'days', true);
        return daysLeft > 0 ? Math.ceil(daysLeft) : 0; // Round up
    })

    const trialDaysLeftPercent = computed((): number => {
        return trialDaysLeft.value === 0 ? 0 : Math.round((trialDaysLeft.value / 14) * 100);
    });

    const trialDaysLeftMessage = computed((): string => {
        if (hasActiveSubscription.value) {
            const plan = subscriptionPlan.value
                ? `${subscriptionPlan.value.charAt(0).toUpperCase()}${subscriptionPlan.value.slice(1)}`
                : 'Unknown';
            return `Membership: ${plan} Plan`;
        }
        if (trialDaysLeft.value <= 0) {
            return 'Your trial has ended';
        }
        return `You have ${trialDaysLeft.value} ${pluralize(trialDaysLeft.value, 'day')} left on your trial.`;
    })

    const trialDaysClass = computed(() => ({
        'text-danger': trialDaysLeft.value === 0,
        'text-warning': trialDaysLeft.value < 2,
    }));

    const currentPlanDetails = computed(() => {
        return subscriptionPlans.value.find((p) => p.name === subscriptionPlan.value);
    });

    const currentPlanBullets = computed(() => {
        return currentPlanDetails.value?.description.bullets;
    });

    const seatLimitReached = computed(() => {
        if (!enforceSubscription.value) return false;

        if (inTrial.value && !hasActiveSubscription.value) {
            return false;
        }

        if (!hasActiveSubscription.value) {
            return true;
        }

        return (planSupportedFeatureCounts.value?.seats !== -1)
            && companyCounts.value?.employees >= planSupportedFeatureCounts.value?.seats;
    })

    const seatLimitReachedMessage = computed(() => {
        if (!enforceSubscription.value) return 'You need a subscription plan to add seats.';
        return `You've reached your max seats of <b>${planSupportedFeatureCounts.value?.seats}</b> for your plan.`;
    });

    const companyListings = computed(() => {
        return companyCounts.value?.listings;
    });

    const listingLimitReachedMessage = computed(() => {
        if (!enforceSubscription.value) return 'You need a subscription plan to add listings.';
        return `You've reached your max listings of <b>${planSupportedFeatureCounts.value?.listings}</b> for your plan.`;
    });

    const listingLimitReached = computed(() => {
        if (!enforceSubscription.value) return false;

        if (inTrial.value && !hasActiveSubscription.value) {
            return false;
        }

        if (!hasActiveSubscription.value) {
            return true;
        }

        return (planSupportedFeatureCounts.value?.listings !== -1)
            && companyCounts.value?.listings >= planSupportedFeatureCounts.value?.listings;
    });

    const planSupportedFeatureCounts = computed<planSupportedFeatureCountsInterface>(() => ({
        listings: resolveFeatureCount('listings'),
        locations: resolveFeatureCount('locations'),
        carSharingConnections: resolveFeatureCount('carSharingConnections'),
        smartCarConnections: resolveFeatureCount('smartCarConnections'),
        orders: resolveFeatureCount('orders'),
        seats: resolveFeatureCount('seats'),
    }));

    const smartCarConnectionsLimitReached = computed(() => {
        if (!enforceSubscription.value) return false;

        if (inTrial.value && !hasActiveSubscription.value) {
            return false;
        }

        if (!hasActiveSubscription.value) {
            return true;
        }

        let connections = 0;

        company.value?.connections?.tesla?.forEach(() => {
            connections++;
        });

        company.value?.connections?.bouncie?.forEach(() => {
            connections++;
        });

        const smartCarLimit = resolveFeatureCount('smartCarConnections');

        return (smartCarLimit !== -1) && connections >= smartCarLimit;
    });

    const carShareConnectionsLimitReached = computed(() => {
        if (!enforceSubscription.value) return false;

        if (inTrial.value && !hasActiveSubscription.value) {
            return false;
        }

        if (!hasActiveSubscription.value) {
            return true;
        }

        let connections = 0;

        company.value?.connections?.turo?.forEach(() => {
            connections++;
        });

        company.value?.connections?.getaround?.forEach(() => {
            connections++;
        });

        company.value?.connections?.ryde?.forEach(() => {
            connections++;
        });

        const carShareLimit = resolveFeatureCount('carSharingConnections');

        return (carShareLimit !== -1) && connections >= carShareLimit;
    });

    const carShareConnectionsLimitReachedMessage = computed(() => {
        if (!enforceSubscription.value) return 'You need a subscription plan to add a connection.';
        return `You've reached your max car-sharing connection of <b>${planSupportedFeatureCounts.value?.carSharingConnections}</b> for your plan.`;
    });

    return {
        subscription,
        seatLimitReached,
        seatLimitReachedMessage,
        subscriptionEndDate,
        currentPlanBullets,
        subscriptionsLoading,
        hasActiveSubscription,
        hasPausedSubscription,
        subscriptionStatus,
        subscriptionPlan,
        subscriptionPlans,
        enforceSubscription,
        customersLoading,
        inTrial,
        isTrialing,
        planIncludesApi,
        planIncludesESign,
        trialEndDate,
        currentStripeSubscriptionObjectRenewalDate,
        currentStripeSubscriptionObjectPlanAmount,
        currentStripeSubscriptionObjectPlanInterval,
        stripeEndDate,
        inGracePeriod,
        trialDaysLeftMessage,
        trialDaysLeft,
        trialDaysLeftPercent,
        trialDaysClass,
        trialEndsMessage,
        companyListings,
        listingLimitReachedMessage,
        listingLimitReached,
        hasPastDueSubscription,
        planSupportedFeatureCounts,
        currentPlanDetails,
        smartCarConnectionsLimitReached,
        carShareConnectionsLimitReached,
        carShareConnectionsLimitReachedMessage,
        addSubscriptionAddon,
        removeSubscriptionAddon,
        resumeSubscription,
        endTrial,
        switchPlan,
        cancelSubscription,
        cancelSubscriptionStripePortal,
        includedInSubscription,
    }
}
